/*!
 * <ss-animation> — host element for scienceSeeds animations — v1.0.0 + scienceSeeds srcdoc change (2026-10-01, see connectedCallback)
 *
 * Shopify: shipped as assets/ss-animation.js and loaded by snippets/ss-animation-embed.liquid.
 * Anywhere else: include this script and write
 *
 *   <ss-animation src="/pages/string-art-helper?view=ss-app-string-art" title="String Art helper"
 *                 height-mode="auto" forward-params min-height="700"></ss-animation>
 *
 * Attributes
 *   src             URL of the app page (required)
 *   title           accessible name for the iframe (required for a11y)
 *   height-mode     "auto" (default): the iframe grows/shrinks with the app, the host page scrolls.
 *                   "viewport": the iframe is one screen tall and the app scrolls inside it
 *                   (keeps position:sticky boards working — see docs/SHOPIFY_INTEGRATION.md).
 *   look            "card" (default) | "bare" (no outer ground colour / padding around the app card)
 *   forward-params  if present, this page's ?query and #hash are passed on to the app
 *                   (e.g. /pages/puzzle-a-day?date=2026-12-25 from the instruction-card QR code).
 *   min-height      placeholder height in px until the app reports its real height (default 600)
 *   lazy            if present, the iframe loads when it approaches the viewport
 *
 * The app side is shared/embed-bridge.js (injected into the app page at build time).
 * Written as a custom element so it re-initialises by itself when the Shopify theme editor
 * adds or re-renders the section (no shopify:section:load handling needed).
 */
(function () {
  'use strict';
  if (window.customElements && customElements.get('ss-animation')) return;

  var live = new Set();
  var SKIP = { view: 1, section_id: 1, sections: 1, preview_theme_id: 1, _ab: 1, _fd: 1, _sc: 1 };

  function buildSrc(el) {
    var url = new URL(el.getAttribute('src'), location.href);
    var mode = el.getAttribute('height-mode') === 'viewport' ? 'viewport' : 'auto';
    url.searchParams.set('ss_height', mode);
    if (el.getAttribute('look') === 'bare') url.searchParams.set('ss_look', 'bare');
    if (el.hasAttribute('forward-params')) {
      new URLSearchParams(location.search).forEach(function (v, k) {
        if (!SKIP[k] && !url.searchParams.has(k)) url.searchParams.set(k, v);
      });
      if (location.hash && !url.hash) url.hash = location.hash;
    }
    return url;
  }

  var raf = 0;
  function sendViewport() {
    raf = 0;
    live.forEach(function (el) {
      var f = el._frame;
      if (!f || !f.contentWindow) return;
      var r = f.getBoundingClientRect();
      try {
        f.contentWindow.postMessage({ type: 'ss-anim:viewport', frameTop: r.top, viewportHeight: window.innerHeight }, el._origin);
      } catch (e) { /* frame navigated elsewhere */ }
    });
  }
  function queue() { if (!raf) raf = requestAnimationFrame(sendViewport); }

  window.addEventListener('message', function (e) {
    var d = e.data;
    if (!d || typeof d !== 'object' || d.type !== 'ss-anim:height') return;
    live.forEach(function (el) {
      if (!el._frame || el._frame.contentWindow !== e.source || e.origin !== el._origin) return;
      if (el.getAttribute('height-mode') !== 'viewport') {
        var h = Math.max(120, Math.min(Math.ceil(+d.height || 0), 20000));
        el._frame.style.height = h + 'px';
      }
      el.setAttribute('ready', '');
    });
  });
  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue);

  class SsAnimation extends HTMLElement {
    connectedCallback() {
      if (this._frame) { live.add(this); return; }          // moved in the DOM: keep the loaded frame
      var url = buildSrc(this);
      var f = document.createElement('iframe');
      f.title = this.getAttribute('title') || 'scienceSeeds animation';
      f.setAttribute('allow', 'fullscreen');
      if (this.hasAttribute('lazy')) f.loading = 'lazy';
      f.style.cssText = 'display:block;width:100%;border:0;background:transparent;';
      f.style.height = this.getAttribute('height-mode') === 'viewport'
        ? 'min(100svh, 1100px)'
        : (parseInt(this.getAttribute('min-height'), 10) || 600) + 'px';
      this._frame = f;
      f.addEventListener('load', queue);                      // handshake: host viewport -> app answers with its height
      this.appendChild(f);
      live.add(this);
      // scienceSeeds change (2026-10-01): Shopify sends X-Frame-Options: DENY / frame-ancestors 'none' on every
      // storefront page, so a same-origin app page can never be framed by URL. Fetch it instead (fetching is not
      // blocked) and load it as srcdoc, which those headers do not apply to. The host options that used to travel
      // in the URL (?ss_height, ?ss_look, forwarded params) are handed over as window.__ssEmbed, which the embed
      // bridge reads first. Cross-origin app hosts (custom URL) still load by URL as before.
      if (url.origin !== location.origin) {
        this._origin = url.origin;
        f.src = url.href;
        return;
      }
      this._origin = location.origin;                         // about:srcdoc inherits the host origin
      var self = this;
      fetch(url.href, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      }).then(function (html) {
        var opts = {};
        url.searchParams.forEach(function (v, k) { opts[k] = v; });
        var inject = '<script>window.__ssEmbed=' + JSON.stringify(opts).replace(/</g, '\\u003c') + ';</script>';
        var i = html.search(/<head[^>]*>/i);
        if (i >= 0) { i = html.indexOf('>', i) + 1; html = html.slice(0, i) + inject + html.slice(i); }
        else html = inject + html;
        f.srcdoc = html;
      }).catch(function (e) {
        self.setAttribute('error', '');
        f.srcdoc = '<p style="font-family:sans-serif;text-align:center;padding:24px">This interactive guide could not be loaded (' + String(e.message || e).replace(/</g, '&lt;') + ').</p>';
      });
    }
    disconnectedCallback() { live.delete(this); }
  }

  customElements.define('ss-animation', SsAnimation);
})();
